import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, TextField, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  getDefaultColumnOrderIds,
  MaterialReactTableProps,
  MRT_Column,
  MRT_ColumnDef,
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  type MRT_ColumnOrderState,
  MRT_Header,
  MRT_RowData,
  MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Hint } from '../govuk/Typography';

export type baseTableProps = {
  columnList?: string[];
  columnVisibility?: Record<string, boolean> | undefined;
  enableRowSelection?: boolean;
  hideDownloadLink?: boolean;
  isClientSide?: boolean;
  onRowSelection?: (rowSelection: MRT_RowSelectionState) => void;
  defaultFilters?: MRT_ColumnFiltersState;
  selectedRows?: MRT_RowSelectionState;
  staticFilters?: MRT_ColumnFiltersState;
};

export type baseListQueryProps = {
  pageIndex: number;
  pageSize: number;
  sorting?: MRT_SortingState;
  columnFilters?: MRT_ColumnFiltersState;
  columnFilterFns?: MRT_ColumnFilterFnsState;
  staticFilters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  isBeneficiary?: boolean;
};

export const defaultCSVConfig = {
  fieldSeparator: ',',
  quoteStrings: true,
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: false,
};

export const defaultMRTConfig: Partial<MaterialReactTableProps<MRT_RowData>> = {
  defaultColumn: {
    size: 100,
    muiFilterDatePickerProps: () => ({
      format: 'DD/MM/YYYY',
    }),
    muiFilterTextFieldProps: ({ column }) => ({
      sx: column.columnDef.filterVariant?.startsWith('date') ? { minWidth: '185px' } : undefined,
    }),
  },
  enableColumnFilterModes: true,
  enableColumnOrdering: true,
  enableColumnResizing: true,
  enableDensityToggle: false,
  enableKeyboardShortcuts: false,
  layoutMode: 'semantic',
  manualFiltering: true,
  manualPagination: true,
  manualSorting: true,
  muiPaginationProps: {
    rowsPerPageOptions: [10, 25, 50, 100],
  },
  muiTablePaperProps: {
    elevation: 0,
  },
  muiTopToolbarProps: {
    className: 'muiToolbarWrapper',
  },
};

export type listQueryType<TData = unknown> = (props: baseListQueryProps) => {
  queryKey: (string | number | MRT_SortingState | MRT_ColumnFiltersState | MRT_ColumnFilterFnsState | undefined)[];
  queryFn: () => Promise<{
    data: TData;
    meta: {
      totalRowCount: number | undefined;
    };
  }>;
  retry: number;
  refetchOnWindowFocus: boolean;
  gcTime?: number;
  staleTime?: number;
};

type SyncColumnFilterFnsStateProps = {
  columnList: string[];
  current: MRT_ColumnFilterFnsState;
  defaultFilterFns: Record<string, string>;
  getColumnId?: (column: string) => string;
};

export const syncColumnFilterFnsState = ({
  columnList,
  current,
  defaultFilterFns,
  getColumnId = (column) => column,
}: SyncColumnFilterFnsStateProps): MRT_ColumnFilterFnsState => {
  const next: MRT_ColumnFilterFnsState = {};

  for (const column of columnList) {
    const columnId = getColumnId(column);
    const currentFn = current[columnId];
    if (currentFn) {
      next[columnId] = currentFn;
      continue;
    }

    const defaultFn = defaultFilterFns[column];
    if (defaultFn) {
      next[columnId] = defaultFn;
    }
  }

  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);
  const isUnchanged = currentKeys.length === nextKeys.length && nextKeys.every((key) => current[key] === next[key]);

  return isUnchanged ? current : next;
};

const defaultFilterFnByVariant: Record<string, string> = {
  range: 'between',
  'date-range': 'between',
  select: 'equals',
  'multi-select': 'equals',
  checkbox: 'equals',
};

type ColumnFilterModeColumnDef = Pick<
  MRT_ColumnDef<MRT_RowData>,
  'id' | 'accessorKey' | 'filterVariant' | 'columnFilterModeOptions'
>;

function getColumnId(column: ColumnFilterModeColumnDef): string | undefined {
  if (typeof column.id === 'string' && column.id.length > 0) {
    return column.id;
  }
  if (typeof column.accessorKey === 'string' && column.accessorKey.length > 0) {
    return column.accessorKey;
  }
  return undefined;
}

function getColumnDefaultFilterMode(column: ColumnFilterModeColumnDef): string {
  const options = column.columnFilterModeOptions;
  if (Array.isArray(options) && options.length > 0 && options[0]) {
    return options[0];
  }

  const variant = column.filterVariant;
  if (variant && defaultFilterFnByVariant[variant]) {
    return defaultFilterFnByVariant[variant];
  }

  return 'contains';
}

export function getDefaultColumnFilterFns(
  columns: ColumnFilterModeColumnDef[],
  explicitDefaults: Record<string, string> = {},
): Record<string, string> {
  const defaults: Record<string, string> = { ...explicitDefaults };

  for (const column of columns) {
    const columnId = getColumnId(column);
    if (!columnId || defaults[columnId]) {
      continue;
    }
    defaults[columnId] = getColumnDefaultFilterMode(column);
  }

  return defaults;
}

type UseSyncColumnFilterFnsStateProps = {
  columns: ColumnFilterModeColumnDef[];
  setColumnFilterFns: Dispatch<SetStateAction<MRT_ColumnFilterFnsState>>;
  defaultFilterFns?: Record<string, string>;
};

export function useSyncColumnFilterFns({
  columns,
  setColumnFilterFns,
  defaultFilterFns,
}: UseSyncColumnFilterFnsStateProps) {
  useEffect(() => {
    const columnIds = columns.map((column) => getColumnId(column)).filter((value): value is string => !!value);

    setColumnFilterFns((current) =>
      syncColumnFilterFnsState({
        columnList: columnIds,
        current,
        defaultFilterFns: getDefaultColumnFilterFns(columns, defaultFilterFns),
      }),
    );
  }, [columns, defaultFilterFns, setColumnFilterFns]);
}

type UseSyncColumnOrderStateProps = {
  columns: MRT_ColumnDef<MRT_RowData>[];
  table: MRT_TableInstance<MRT_RowData>;
  setColumnOrder: Dispatch<SetStateAction<MRT_ColumnOrderState>>;
};

export function useSyncColumnOrder({ columns, table, setColumnOrder }: UseSyncColumnOrderStateProps) {
  useEffect(() => {
    setColumnOrder((current) => {
      const next = getDefaultColumnOrderIds(table.options, true);
      const isUnchanged = next.length === current.length && next.every((id) => current.includes(id));
      return isUnchanged ? current : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, setColumnOrder]);
}

export const tableRowSelectionToIDArray = (rs: MRT_RowSelectionState) => {
  const idArray: number[] = [];
  for (const key in rs) {
    if (Object.prototype.hasOwnProperty.call(rs, key)) {
      const parsedKey = parseInt(key, 10);
      if (!isNaN(parsedKey)) {
        idArray.push(parsedKey);
      }
    }
  }
  return idArray;
};

type DateRangeFilterProps = {
  column: MRT_Column<MRT_RowData, unknown>;
  header: MRT_Header<MRT_RowData>;
  rangeFilterIndex?: number;
  table: MRT_TableInstance<MRT_RowData>;
};
export const DateRangeFilter = (filter: DateRangeFilterProps) => {
  const column = filter.column;
  const filterMode = filter.table.getState().columnFilterFns?.[column.id];
  const isEmptyMode = filterMode === 'empty' || filterMode === 'notEmpty';

  if (isEmptyMode) {
    if (filter.rangeFilterIndex === 1) {
      return null;
    }

    return <Hint>{filterMode === 'empty' ? 'Empty' : 'Not empty'}</Hint>;
  }

  return (
    <DatePicker
      format="DD/MM/YYYY"
      onChange={(newValue) => {
        const value = newValue ? newValue.format('YYYY-MM-DD') : null;
        column.setFilterValue((old: [object | undefined, object | undefined]) => {
          if (!Array.isArray(old)) {
            old = [undefined, undefined];
          }
          if (filter.rangeFilterIndex === 0) {
            return [value, old[1]];
          } else {
            return [old[0], value];
          }
        });
      }}
      slotProps={{
        textField: {
          sx: { minWidth: '185px' },
          variant: 'standard',
        },
      }}
    />
  );
};

export const NumberRangeFilter = (filter: DateRangeFilterProps) => {
  const column = filter.column;
  const filterMode = filter.table.getState().columnFilterFns?.[column.id];
  const isEmptyMode = filterMode === 'empty' || filterMode === 'notEmpty';

  if (isEmptyMode) {
    if (filter.rangeFilterIndex === 1) {
      return null;
    }

    return <Hint>{filterMode === 'empty' ? 'Empty' : 'Not empty'}</Hint>;
  }

  return (
    <TextField
      type="number"
      onChange={(e) => {
        const value = e.target.value;
        column.setFilterValue((old: [string | undefined, string | undefined]) => {
          if (!Array.isArray(old)) {
            old = [undefined, undefined];
          }
          if (filter.rangeFilterIndex === 0) {
            return [value, old[1]];
          }
          return [old[0], value];
        });
      }}
      variant="standard"
      sx={{ minWidth: '138px' }}
    />
  );
};

type TableToolbarInternalActionsProps = {
  table: MRT_TableInstance<MRT_RowData>;
  refetch: () => void;
  enableGlobalFilter?: boolean;
};

export const TableToolbarInternalActions = ({
  table,
  refetch,
  enableGlobalFilter = true,
}: TableToolbarInternalActionsProps) => {
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown === 0) return;

    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  return (
    <>
      <Tooltip title="Refresh">
        <IconButton
          onClick={() => {
            setCooldown(30);
            refetch();
          }}
          disabled={cooldown > 0}
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      {enableGlobalFilter && <MRT_ToggleGlobalFilterButton table={table} />}
      <MRT_ToggleFiltersButton table={table} />
      <MRT_ShowHideColumnsButton table={table} />
      <MRT_ToggleFullScreenButton table={table} />
    </>
  );
};
